import { Box, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import type { FC } from 'react';
import type { DuplicateDepositInfo } from 'constants/pending-requests.constants';
import {
	DUPLICATE_POPUP_MAX_HEIGHT,
	DUPLICATE_DEPOSIT_BOX_BG,
	DUPLICATE_DEPOSIT_BOX_BG_HOVER,
} from 'constants/pending-requests.constants';
import { pagesEnum } from 'constants/ui.constants';

const getDuplicateStatusLabel = (status: string, t: (key: string) => string): string => {
	const baseKey = 'page.pendingRequests.duplicateBalancePopup.status.';
	const key = `${baseKey}${status}`;
	const keyCap = `${baseKey}${
		typeof status === 'string' ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : status
	}`;
	if (t(key) !== key) return t(key);
	if (t(keyCap) !== keyCap) return t(keyCap);
	return status;
};

export const DuplicateDepositPopupContent: FC<{
	digitalBalanceAdditions: DuplicateDepositInfo[];
	t: (key: string) => string;
}> = ({ digitalBalanceAdditions, t }) => (
	<Stack spacing={1.5}>
		<Box
			sx={{
				maxHeight: DUPLICATE_POPUP_MAX_HEIGHT,
				minHeight: 0,
				overflowY: 'auto',
				direction: 'ltr',
			}}
		>
			<Stack spacing={1.5}>
				{digitalBalanceAdditions.map((d) => (
					<Box
						key={d.id}
						role="link"
						tabIndex={0}
						onClick={(e) => {
							e.stopPropagation();
							window.open(`/${pagesEnum.COMPANY_FEATURE_PROFILE}/balancesRequests/${d.id}`, '_blank');
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								window.open(`/${pagesEnum.COMPANY_FEATURE_PROFILE}/balancesRequests/${d.id}`, '_blank');
							}
						}}
						sx={{
							bgcolor: DUPLICATE_DEPOSIT_BOX_BG,
							borderRadius: 1,
							px: 1.5,
							py: 1,
							cursor: 'pointer',
							'&:hover': { bgcolor: DUPLICATE_DEPOSIT_BOX_BG_HOVER },
						}}
					>
						<Stack spacing={0.5}>
							<Typography variant="body2">
								{`${d.currencySymbol || ''}${d.amount?.toLocaleString() ?? ''} ${d.currencyCode ?? ''}`}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{t('page.pendingRequests.field.createdAt')}:{' '}
								{format(new Date(d.createdAt), 'dd.MM.yyyy HH:mm')}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{t('page.pendingRequests.field.reference')}: {d.reference ?? '-'}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{t('page.pendingRequests.field.status')}: {getDuplicateStatusLabel(d.status, t)}
							</Typography>
						</Stack>
					</Box>
				))}
			</Stack>
		</Box>
		<Typography variant="body2" color="text.secondary">
			{t('page.pendingRequests.duplicateBalancePopup.awarenessMessage')}
		</Typography>
	</Stack>
);
